import React, { useCallback, useEffect, useRef, useState } from "react";
import { CustomButton } from "../components/custom-button";
import { useNavigate } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Switch,
  FormControlLabel,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../store/store";
import {
  getWebSocketInstance,
  loginUser,
  setIsLoggingIn,
  handleLoginResponse,
  setLoginError,

  setLoginSuccess,
} from "../store/slices/userSlice";
import { user_login_response, user_credentials } from "../models/protobuff-objects/finxai_user_pb";
import { FinxAiUser } from "../models/finxaiUser";
import { clearAllCache, setCache } from "../utils/cache";
import finxAiLogo from "../assets/logos/finxai_logo_svg_only.svg";
import logger from "../utils/logger";
import { FinxAiFooter } from "../components/finxai-footer";
import {
  CustomChartComponent,
  dummyChartData,
} from "../components/custom-chart";
import { jsonToMap } from "../utils/helpers";
import DataGridWebSocketService from "../api/services/data-grid-websocket-service";

export const LoginView = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { isLoggingIn, loginError, loggedInUser } =
    useSelector((state: RootState) => state.user);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [username, setUsernameState] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isQa, setIsQa] = useState<boolean>(false);
  const [isResetMode, setIsResetMode] = useState<boolean>(false);
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [resetSuccess, setResetSuccess] = useState<string | null>(null);
  const [agreedToTerms, setAgreedToTerms] = useState<boolean>(false);
  const usernameInputRef = useRef<HTMLInputElement>(null);

  const userCredential = new user_credentials();
  const userCredentialRef = useRef<user_credentials>();
  userCredentialRef.current = userCredential;

  const navigate = useNavigate();
  const loginBrandHeader = process.env.REACT_APP_LOGIN_TEXT || "";
  //const disclaimerShort = process.env.REACT_APP_DISCLAIMER_SHORT || "";
  const disclaimerLongFilePath =
    process.env.REACT_APP_DISCLAIMER_LONG_FILE_PATH || "";

  // Handle dialog open/close
  const openDisclaimer = () => setShowDisclaimer(true);
  const closeDisclaimer = () => setShowDisclaimer(false);

  const cacheUser = (user: FinxAiUser) => {
    setCache("loggedInUser", user, 7);
  };

  // Create a WebSocket connection
  useEffect(() => {
    DataGridWebSocketService.sendOnMessage(async (event) => {
      logger.debug("Received message from LOGINVIEW");

      const ws = getWebSocketInstance();
      const response = await ws.handleBinaryMessage(event.data);

      if (response instanceof user_login_response) {
        logger.debug("Received user_login_response payload");
        logger.debug("user_login_response payload: ", response);
        if (response.getIsLoggedin()) {
          logger.debug("Loggin successful, navigating.");

          // Check if this was a password reset request
          if (isResetMode && newPassword) {
            // Password reset successful
            setResetSuccess("Password reset successful! Please login with your new password.");
            setIsResetMode(false);
            setPassword("");
            setNewPassword("");
            setConfirmPassword("");
            dispatch(setLoginError(""));
            dispatch(setIsLoggingIn(false));
            return;
          }

          dispatch(handleLoginResponse(response));
          /// build and hold the user object in user store
          const currentUser = ws.builUserObject(response, username, password);
          /// Populate user preferences
          // dispatch(setUserPreferences(JSON.parse(response.getUserPreferences())))
          logger.info("The login object is: ", response.getUserPreferences());

          const rawPreferencesjsonObject = JSON.parse(
            response.getUserPreferences()
          );
          logger.info("raw json: ", rawPreferencesjsonObject);
          const userPreferencesMap = new Map<string, any>(Object.entries(rawPreferencesjsonObject));
          logger.info("Converted json to map: ", userPreferencesMap)


          cacheUser(currentUser);
          dispatch(setLoginSuccess(currentUser));
          navigate("/home");
        } else {
          // Check if this was a password reset request
          if (isResetMode && newPassword) {
            dispatch(
              setLoginError("Password reset failed. Current password may be incorrect.")
            );
          } else {
            dispatch(
              setLoginError("Username or Password Incorrect. Login Failed.")
            );
          }
        }
      } else if (response instanceof user_credentials) {

        logger.debug("Received user_credentials payload");

        // Get cached user
        const cachedUserStr = localStorage.getItem("loggedInUser");
        if (!cachedUserStr) return;

        const cachedUser: FinxAiUser = JSON.parse(cachedUserStr);

        // Update role
        const role = response.getRole();
        cachedUser.userRole = role;

        // Re-cache updated user with role
        setCache("loggedInUser", cachedUser, 7);
        logger.info("Updated cached user with role:", role);

        dispatch(setLoginSuccess(cachedUser)); // Re-dispatch updated user if needed


      }

    })
  }, [isResetMode, newPassword, username, password, dispatch, navigate])

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setUsernameState(e.target.value);
  };

  const handlePasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setPassword(e.target.value);
  };

  const handleNewPasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setNewPassword(e.target.value);
  };

  const handleConfirmPasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setConfirmPassword(e.target.value);
  };

  const toggleResetMode = (): void => {
    setIsResetMode(!isResetMode);
    setPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setResetSuccess(null);
    dispatch(setLoginError(""));
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setIsQa(e.target.checked);
  };

  const handleLogin = useCallback(async () => {
    if (isResetMode) {
      // Validate password reset form
      if (!username || !password || !newPassword || !confirmPassword) {
        dispatch(setLoginError("All fields are required for password reset."));
        return;
      }
      if (newPassword !== confirmPassword) {
        dispatch(setLoginError("New password and confirm password do not match."));
        return;
      }
      if (newPassword === password) {
        dispatch(setLoginError("New password must be different from current password."));
        return;
      }
    } else {
      // Validate login form
      if (!username || !password) {
        return;
      }
    }

    try {
      const loginRequestMessage = await dispatch(
        loginUser({
          username: username,
          password: password,
          isQa: isQa.toString(),
          newPasswd: isResetMode ? newPassword : undefined,
        })
      );

      // Send the login request message via WebSocket
      DataGridWebSocketService.sendMessage(loginRequestMessage.serializeBinary());
    } catch (error) {
      console.error("Login failed:", error);
      dispatch(setIsLoggingIn(false));
    }
  }, [username, password, isQa, isResetMode, newPassword, confirmPassword, dispatch]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    handleLogin();
  };

  const isFormValid = isResetMode
    ? username !== "" && password !== "" && newPassword !== "" && confirmPassword !== ""
    : username !== "" && password !== "" && agreedToTerms;

  /// Autofocus on username input 
  useEffect(() => {
    if (usernameInputRef.current) {
      usernameInputRef.current.focus();
    }
  }, []);

  //handle regisration
  const handleRegister = () => {
    navigate('/register')
  }

  const handleFind = (): boolean => {
    alert('Please contact tech support (support@finxai.us) to recover your username or password')
    return false;
  }

  return (
    <div className="justify-center items-center flex flex-col h-screen">
      <h1 className="text-3xl font-bold pb-3 text-white">{loginBrandHeader}</h1>
      <div className="logincard min-w-72 bg-primaryTableBg justify-center items-center flex flex-col p-5 shadow-xl rounded-xl">
        <div className="justify-center items-center flex flex-row">
          {/* <img
            className="w-10 h-10 mb-3 mx-1"
            src={finxAiLogo}
            alt="FinxAi Logo"
          /> */}
          <h1 className="text-2xl font-semibold pb-3 text-gray-300">
            {isResetMode ? "Reset Password" : "Login"}
          </h1>
        </div>
        <form className="flex flex-col w-full" onSubmit={handleSubmit}>
          <input
            ref={usernameInputRef}
            className="username text-sm bg-inputFieldBg rounded-xl p-2 mb-3 text-white"
            type="text"
            placeholder="Username"
            onChange={handleEmailChange}
            value={username}
          />
          <div className="relative">
            <input
              className="password text-sm bg-inputFieldBg rounded-xl p-2 text-white w-full"
              type={showPassword ? "text" : "password"}
              placeholder={isResetMode ? "Current Password" : "Password"}
              value={password}
              onChange={handlePasswordChange}
            />
            <button
              type="button"
              className="scale-75 absolute right-2 top-1 text-cellTextFontColor text-sm"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
            </button>
          </div>
          {isResetMode && (
            <>
              <div className="relative mt-3">
                <input
                  className="password text-sm bg-inputFieldBg rounded-xl p-2 text-white w-full"
                  type={showNewPassword ? "text" : "password"}
                  placeholder="New Password"
                  value={newPassword}
                  onChange={handleNewPasswordChange}
                />
                <button
                  type="button"
                  className="scale-75 absolute right-2 top-1 text-cellTextFontColor text-sm"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </button>
              </div>
              <div className="relative mt-3">
                <input
                  className="password text-sm bg-inputFieldBg rounded-xl p-2 text-white w-full"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm New Password"
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                />
                <button
                  type="button"
                  className="scale-75 absolute right-2 top-1 text-cellTextFontColor text-sm"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </button>
              </div>
            </>
          )}
          <div className="text-gray-300 text-sm mt-4 text-left">
            {isResetMode ? (
              <button
                type="button"
                onClick={toggleResetMode}
                className="text-blue-500 underline"
              >
                Back to Login
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={toggleResetMode}
                  className="text-blue-500 underline mr-4"
                >
                  Reset Password
                </button>
                <button
                  type="button"
                  onClick={() => { return handleFind() }}
                  className="text-blue-500 underline"
                >
                  Forgot username/password?
                </button>
              </>
            )}
          </div>

          {!isResetMode && (
            <div className="flex items-center mt-3">
              <input
                type="checkbox"
                id="agreeToTerms"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                className="mr-2 accent-primary"
              />
              <label htmlFor="agreeToTerms" className="text-gray-300 text-sm">
                I agree to the{" "}
                <a
                  href="/DVSecuritiesTermsofUse.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500 underline"
                >
                  Terms of Use
                </a>
              </label>
            </div>
          )}

          <div
            className={`transition-opacity duration-300 ease-in-out ${isFormValid ? "opacity-100 mt-2" : "opacity-50"
              }`}
          >
            {isLoggingIn ? (
              <CircularProgress color="secondary" />
            ) : (
              <CustomButton
                className={`${!isFormValid ? "opacity-50 cursor-not-allowed" : ""}`}
                label={isResetMode ? "Reset Password" : "Login"}
                onClick={() => { }}
                disabled={!isFormValid}
              />
            )}
          </div>
        </form>
        {resetSuccess && ( // Display success message for password reset
          <div className="mt-4 text-green-500 text-sm">{resetSuccess}</div>
        )}
        {loginError && loginError !== "" && ( // Display error message if loginError is not null or empty
          <div className="mt-4 text-red-500 text-sm">{loginError}</div>
        )}
      </div>

      <div className="text-gray-300 text-sm mt-4 text-center">
        {disclaimerLongFilePath && (
          <span>
            &nbsp;
            <button
              onClick={openDisclaimer}
              className="text-blue-500 underline"
            >
              Disclaimer
            </button>
          </span>
        )}
      </div>

      {/* Disclaimer Dialog */}
      <Dialog
        open={showDisclaimer}
        onClose={closeDisclaimer}
        sx={{
          "& .MuiPaper-root": {
            borderRadius: "15px",
            backgroundColor: "#27406A",
            boxShadow: 5, // Adds elevation
          },
        }}
      >
        <DialogTitle style={{ color: "#FFFFFF" }}>Disclaimer</DialogTitle>
        <DialogContent>
          <iframe
            src={disclaimerLongFilePath}
            title="Disclaimer"
            style={{ width: "100%", height: "500px", borderRadius: "10px" }}
          />
        </DialogContent>
      </Dialog>
      <div></div>
      <FinxAiFooter />
    </div>
  );
};
